import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPlatformTenantUser,
  getPlatformTenantModules,
  getPlatformOverview,
  getPlatformTicket,
  listPlatformSubscriptions,
  listPlatformTenantUsers,
  listPlatformTenants,
  listPlatformTickets,
  logoutPlatformAdmin,
  PLATFORM_SESSION_REPLACED_EVENT,
  PLATFORM_SESSION_REPLACED_MESSAGE,
  replyPlatformTicket,
  resetPlatformTenantUserPassword,
  restorePlatformAdminSession,
  startPlatformAdminHeartbeat,
  updatePlatformSubscription,
  updatePlatformTenantLicense,
  updatePlatformTenantModules,
  updatePlatformTenantPlan,
  updatePlatformTenantStatus,
  updatePlatformTenantUser,
  updatePlatformTicket
} from '../services/platformAdminClient';
import {
  ERP_USERNAME_HELP,
  isValidErpUsername,
  normalizeErpUsername
} from '../services/erpUsername';
import PlatformBannerManager from '../components/PlatformBannerManager';
import PlatformAffiliateManager from '../components/PlatformAffiliateManager';
import '../styles/PlatformOwnerPanel.css';

const TABS = Object.freeze([
  { id: 'overview', label: 'Visão geral', icon: 'fa-chart-line' },
  { id: 'tenants', label: 'Clientes', icon: 'fa-building' },
  { id: 'subscriptions', label: 'Assinaturas', icon: 'fa-credit-card' },
  { id: 'affiliates', label: 'Afiliados', icon: 'fa-handshake' },
  { id: 'banners', label: 'Banners comerciais', icon: 'fa-rectangle-ad' },
  { id: 'tickets', label: 'Tickets', icon: 'fa-headset' }
]);

const TENANT_STATUS = Object.freeze({ active: 'Ativo', suspended: 'Suspenso', inactive: 'Inativo' });
const LICENSE_STATUS = Object.freeze({ active: 'Ativa', suspended: 'Suspensa', cancelled: 'Cancelada' });
const PERSISTENCE_MODE = Object.freeze({ cloud_authoritative: 'Nuvem · 100% web', local: 'Local · SQLite/offline' });
const SUBSCRIPTION_STATUS = Object.freeze({
  trialing: 'Em avaliação', active: 'Ativa', past_due: 'Em atraso', suspended: 'Suspensa', cancelled: 'Cancelada'
});
const TICKET_STATUS = Object.freeze({
  open: 'Aberto', in_progress: 'Em atendimento', waiting_customer: 'Aguardando cliente', resolved: 'Resolvido', closed: 'Fechado'
});
const TICKET_PRIORITY = Object.freeze({ low: 'Baixa', normal: 'Normal', high: 'Alta', urgent: 'Urgente' });
const PLAN_LABELS = Object.freeze({ basic: 'Local', performance: 'Nuvem', enterprise: 'Enterprise' });
const TENANT_USER_ROLES = Object.freeze({ owner: 'Proprietário', manager: 'Gestor', operator: 'Operador', accountant: 'Contador' });
const TENANT_USER_STATUS = Object.freeze({ active: 'Ativo', blocked: 'Bloqueado', inactive: 'Inativo' });
const MODULE_CATEGORY_LABELS = Object.freeze({
  essential: 'Essenciais',
  operations: 'Operação',
  management: 'Gestão',
  compliance: 'Fiscal e conformidade',
  integrations: 'Integrações'
});
const TENANT_EDITOR_TABS = Object.freeze([
  { id: 'account', label: 'Conta e licença', icon: 'fa-building-shield' },
  { id: 'users', label: 'Usuários', icon: 'fa-users-gear' },
  { id: 'modules', label: 'Módulos', icon: 'fa-puzzle-piece' }
]);

function itemsFrom(payload, key) {
  if (Array.isArray(payload)) return payload;
  return payload?.items || payload?.[key] || [];
}

function userFromActor(actor) {
  return actor?.user || actor?.platformUser || actor?.platform_user || actor || {};
}

function valueFrom(source, ...keys) {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return undefined;
}

function navigateTo(path, onNavigate) {
  if (typeof onNavigate === 'function') onNavigate(path);
  else window.location.assign(path);
}

export function formatPlatformMoney(cents) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format(Number(cents || 0) / 100);
}

export function formatPlatformDate(value, withTime = false) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return withTime ? date.toLocaleString('pt-BR') : date.toLocaleDateString('pt-BR');
}

export function priceInputToCents(value) {
  const raw = String(value ?? '').trim().replace(/\s/g, '');
  // Com vírgula, assume formato brasileiro (1.234,56). Sem vírgula, o ponto
  // é decimal, para que 49.00 continue significando R$ 49,00.
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const amount = Number(normalized);
  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : NaN;
}

function dateInput(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

function isoEndOfDay(value) {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function tenantView(item) {
  const license = item?.license || {};
  return {
    ...item,
    id: valueFrom(item, 'id', 'tenantId', 'tenant_id'),
    displayName: valueFrom(item, 'displayName', 'display_name', 'name', 'legalName', 'legal_name') || 'Empresa sem nome',
    legalName: valueFrom(item, 'legalName', 'legal_name'),
    slug: valueFrom(item, 'slug') || '—',
    status: valueFrom(item, 'status') || 'inactive',
    planCode: valueFrom(item, 'planCode', 'plan_code') || valueFrom(license, 'planCode', 'plan_code') || 'basic',
    licenseStatus: valueFrom(item, 'licenseStatus', 'license_status') || valueFrom(license, 'status') || 'active',
    includedSeats: Number(valueFrom(item, 'includedSeats', 'included_seats') ?? valueFrom(license, 'includedSeats', 'included_seats') ?? 6),
    extraSeats: Number(valueFrom(item, 'extraSeats', 'extra_seats') ?? valueFrom(license, 'extraSeats', 'extra_seats') ?? 0),
    persistenceMode: valueFrom(item, 'persistenceMode', 'persistence_mode') || valueFrom(license, 'persistenceMode', 'persistence_mode') || 'cloud_authoritative',
    occupiedSeats: Number(valueFrom(item, 'activeUsers', 'active_users', 'occupiedSeats', 'occupied_seats') ?? valueFrom(license, 'occupiedSeats', 'occupied_seats') ?? 0),
    billingReference: valueFrom(item, 'billingReference', 'billing_reference') || valueFrom(license, 'billingReference', 'billing_reference') || ''
  };
}

function subscriptionView(item) {
  const tenant = item?.tenant || {};
  return {
    ...item,
    id: valueFrom(item, 'id'),
    tenantId: valueFrom(item, 'tenantId', 'tenant_id') || valueFrom(tenant, 'id'),
    tenantName: valueFrom(item, 'tenantName', 'tenant_name') || valueFrom(tenant, 'displayName', 'display_name', 'name') || 'Empresa',
    planCode: valueFrom(item, 'planCode', 'plan_code') || 'basic',
    billingCycle: valueFrom(item, 'billingCycle', 'billing_cycle') || 'monthly',
    priceCents: Number(valueFrom(item, 'priceCents', 'price_cents') || 0),
    status: valueFrom(item, 'status') || 'trialing',
    currentPeriodEndsAt: valueFrom(item, 'currentPeriodEndsAt', 'current_period_ends_at'),
    startsAt: valueFrom(item, 'startsAt', 'starts_at'),
    provider: valueFrom(item, 'provider') || '',
    providerSubscriptionId: valueFrom(item, 'providerSubscriptionId', 'provider_subscription_id') || ''
  };
}

function ticketView(item) {
  const tenant = item?.tenant || {};
  return {
    ...item,
    id: valueFrom(item, 'id'),
    number: valueFrom(item, 'ticketNumber', 'ticket_number', 'number'),
    subject: valueFrom(item, 'subject') || 'Chamado sem assunto',
    tenantName: valueFrom(item, 'tenantName', 'tenant_name') || valueFrom(tenant, 'displayName', 'display_name', 'name') || 'Empresa',
    tenantId: valueFrom(item, 'tenantId', 'tenant_id') || valueFrom(tenant, 'id'),
    status: valueFrom(item, 'status') || 'open',
    priority: valueFrom(item, 'priority') || 'normal',
    lastMessageAt: valueFrom(item, 'lastMessageAt', 'last_message_at', 'updatedAt', 'updated_at'),
    createdAt: valueFrom(item, 'createdAt', 'created_at')
  };
}

function tenantUserView(item) {
  return {
    ...item,
    id: valueFrom(item, 'id', 'userId', 'user_id'),
    username: valueFrom(item, 'username') || '—',
    displayName: valueFrom(item, 'displayName', 'display_name') || valueFrom(item, 'username') || 'Usuário',
    role: valueFrom(item, 'role') || 'operator',
    status: valueFrom(item, 'status') || 'inactive',
    lastLoginAt: valueFrom(item, 'lastLoginAt', 'last_login_at'),
    lockedUntil: valueFrom(item, 'lockedUntil', 'locked_until'),
    online: Boolean(valueFrom(item, 'online', 'isOnline', 'is_online'))
  };
}

function tenantModuleView(item) {
  const key = valueFrom(item, 'key', 'code', 'moduleKey', 'module_key');
  const required = Boolean(valueFrom(item, 'required', 'isRequired', 'is_required'));
  const rawCategory = valueFrom(item, 'category', 'group') || 'operations';
  return {
    ...item,
    key,
    name: valueFrom(item, 'name', 'label', 'displayName', 'display_name') || key || 'Módulo',
    description: valueFrom(item, 'description') || '',
    category: MODULE_CATEGORY_LABELS[rawCategory] || rawCategory,
    required,
    enabled: required || Boolean(valueFrom(item, 'enabled', 'active', 'isEnabled', 'is_enabled')),
    source: valueFrom(item, 'source') || 'tenant'
  };
}

function tenantLicenseView(payload, fallbackTenant) {
  const license = payload?.license || payload?.seats || {};
  const includedSeats = Number(valueFrom(license, 'includedSeats', 'included_seats') ?? fallbackTenant.includedSeats ?? 6);
  const extraSeats = Number(valueFrom(license, 'extraSeats', 'extra_seats') ?? fallbackTenant.extraSeats ?? 0);
  const capacity = Number(valueFrom(license, 'capacity', 'totalSeats', 'total_seats') ?? includedSeats + extraSeats);
  const occupiedSeats = Number(valueFrom(license, 'occupiedSeats', 'occupied_seats') ?? fallbackTenant.occupiedSeats ?? 0);
  return {
    status: valueFrom(license, 'status') || fallbackTenant.licenseStatus,
    includedSeats,
    extraSeats,
    capacity,
    occupiedSeats,
    availableSeats: Math.max(0, Number(valueFrom(license, 'availableSeats', 'available_seats') ?? capacity - occupiedSeats))
  };
}

function tenantUserIsTemporarilyLocked(user) {
  const lockedUntil = user?.lockedUntil ? new Date(user.lockedUntil) : null;
  return Boolean(lockedUntil && !Number.isNaN(lockedUntil.getTime()) && lockedUntil.getTime() > Date.now());
}

function tenantUserActivity(user) {
  if (tenantUserIsTemporarilyLocked(user)) return `Bloqueado até ${formatPlatformDate(user.lockedUntil, true)}`;
  return user.online ? 'Online agora' : formatPlatformDate(user.lastLoginAt, true);
}

function StatusPill({ value, labels, kind = 'neutral' }) {
  return <span className={`platform-status is-${value || kind}`}>{labels[value] || value || 'Não informado'}</span>;
}

function Feedback({ value, onDismiss }) {
  if (!value?.text) return null;
  return (
    <div className={`platform-feedback is-${value.type || 'info'}`} role={value.type === 'error' ? 'alert' : 'status'}>
      <i className={`fas ${value.type === 'error' ? 'fa-circle-exclamation' : value.type === 'success' ? 'fa-circle-check' : 'fa-circle-info'}`} aria-hidden="true" />
      <span>{value.text}</span>
      {onDismiss && <button type="button" onClick={onDismiss} aria-label="Fechar aviso"><i className="fas fa-xmark" aria-hidden="true" /></button>}
    </div>
  );
}

function EmptyState({ icon, title, children }) {
  return (
    <div className="platform-empty-state">
      <i className={`fas ${icon}`} aria-hidden="true" />
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

export default function OwnerPanel({
  actor: suppliedActor = null,
  onSessionReady,
  onSessionEnded,
  onNavigate
}) {
  const [actor, setActor] = useState(suppliedActor);
  const [checkingSession, setCheckingSession] = useState(!suppliedActor);
  const [sessionFailure, setSessionFailure] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [loadingSection, setLoadingSection] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [tenantFilters, setTenantFilters] = useState({ q: '', status: '', plan: '' });
  const [subscriptionFilters, setSubscriptionFilters] = useState({ status: '', plan: '' });
  const [ticketFilters, setTicketFilters] = useState({ q: '', status: '', priority: '' });
  const [bannerRefreshToken, setBannerRefreshToken] = useState(0);
  const [affiliateRefreshToken, setAffiliateRefreshToken] = useState(0);

  const endSession = useCallback((message = PLATFORM_SESSION_REPLACED_MESSAGE) => {
    setActor(null);
    setSessionFailure(message);
    if (typeof onSessionEnded === 'function') onSessionEnded(message);
  }, [onSessionEnded]);

  useEffect(() => {
    if (suppliedActor) {
      setActor(suppliedActor);
      setCheckingSession(false);
      return undefined;
    }
    let active = true;
    restorePlatformAdminSession()
      .then((restored) => {
        if (!active) return;
        if (!restored) {
          navigateTo('/owner/login', onNavigate);
          return;
        }
        setActor(restored);
        if (typeof onSessionReady === 'function') onSessionReady(restored);
      })
      .catch((error) => {
        if (!active) return;
        if (error?.code === 'session_replaced') endSession(PLATFORM_SESSION_REPLACED_MESSAGE);
        else setSessionFailure(error?.message || 'Não foi possível restaurar a sessão administrativa.');
      })
      .finally(() => { if (active) setCheckingSession(false); });
    return () => { active = false; };
  }, [endSession, onNavigate, onSessionReady, suppliedActor]);

  useEffect(() => {
    if (!actor) return undefined;
    return startPlatformAdminHeartbeat({
      onSessionReplaced: () => endSession(PLATFORM_SESSION_REPLACED_MESSAGE),
      onError: (error) => {
        if (error?.status === 401) {
          endSession(error?.code === 'session_replaced'
            ? PLATFORM_SESSION_REPLACED_MESSAGE
            : 'Sua sessão administrativa expirou. Entre novamente.');
          return;
        }
        if (error?.code !== 'network_unavailable' && error?.code !== 'session_replaced') {
          setFeedback({ type: 'error', text: error?.message || 'Falha ao validar a sessão.' });
        }
      }
    });
  }, [actor, endSession]);

  useEffect(() => {
    const replaced = () => endSession(PLATFORM_SESSION_REPLACED_MESSAGE);
    window.addEventListener(PLATFORM_SESSION_REPLACED_EVENT, replaced);
    return () => window.removeEventListener(PLATFORM_SESSION_REPLACED_EVENT, replaced);
  }, [endSession]);

  const loadOverview = useCallback(async () => {
    setLoadingSection(true);
    try {
      setOverview(await getPlatformOverview());
    } catch (error) {
      if (error?.code === 'session_replaced') return;
      setFeedback({ type: 'error', text: error.message });
    } finally {
      setLoadingSection(false);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    setLoadingSection(true);
    try {
      const result = await listPlatformTenants({ ...tenantFilters, limit: 100, offset: 0 });
      setTenants(itemsFrom(result, 'tenants').map(tenantView));
    } catch (error) {
      if (error?.code !== 'session_replaced') setFeedback({ type: 'error', text: error.message });
    } finally {
      setLoadingSection(false);
    }
  }, [tenantFilters]);

  const loadSubscriptions = useCallback(async () => {
    setLoadingSection(true);
    try {
      const [result, tenantResult] = await Promise.all([
        listPlatformSubscriptions({ ...subscriptionFilters, limit: 100, offset: 0 }),
        listPlatformTenants({ limit: 100, offset: 0 })
      ]);
      setSubscriptions(itemsFrom(result, 'subscriptions').map(subscriptionView));
      setTenants(itemsFrom(tenantResult, 'tenants').map(tenantView));
    } catch (error) {
      if (error?.code !== 'session_replaced') setFeedback({ type: 'error', text: error.message });
    } finally {
      setLoadingSection(false);
    }
  }, [subscriptionFilters]);

  const loadBannerTenants = useCallback(async () => {
    setLoadingSection(true);
    try {
      const result = await listPlatformTenants({ limit: 100, offset: 0 });
      setTenants(itemsFrom(result, 'tenants').map(tenantView));
    } catch (error) {
      if (error?.code !== 'session_replaced') setFeedback({ type: 'error', text: error.message });
    } finally {
      setLoadingSection(false);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoadingSection(true);
    try {
      const result = await listPlatformTickets({ ...ticketFilters, limit: 100, offset: 0 });
      setTickets(itemsFrom(result, 'tickets').map(ticketView));
    } catch (error) {
      if (error?.code !== 'session_replaced') setFeedback({ type: 'error', text: error.message });
    } finally {
      setLoadingSection(false);
    }
  }, [ticketFilters]);

  useEffect(() => {
    if (!actor) return;
    if (activeTab === 'overview') void loadOverview();
    if (activeTab === 'tenants') void loadTenants();
    if (activeTab === 'subscriptions') void loadSubscriptions();
    if (activeTab === 'banners') void loadBannerTenants();
    if (activeTab === 'tickets') void loadTickets();
  }, [activeTab, actor, loadBannerTenants, loadOverview, loadSubscriptions, loadTenants, loadTickets]);

  const signOut = async () => {
    try {
      await logoutPlatformAdmin();
    } catch (_error) {
      // A memória da sessão é limpa pelo cliente mesmo sem resposta do servidor.
    }
    setActor(null);
    if (typeof onSessionEnded === 'function') onSessionEnded('logout');
    navigateTo('/owner/login', onNavigate);
  };

  const openTicket = async (ticket) => {
    setSelectedTicket({ ...ticket, loading: true });
    try {
      const detail = await getPlatformTicket(ticket.id);
      const normalizedTicket = ticketView(detail?.ticket || detail);
      setSelectedTicket({ ...normalizedTicket, messages: itemsFrom(detail, 'messages') });
    } catch (error) {
      setSelectedTicket(null);
      setFeedback({ type: 'error', text: error.message });
    }
  };

  const user = userFromActor(actor);

  if (checkingSession) {
    return <div className="platform-owner-loading" role="status"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Validando acesso Nivora ERP...</div>;
  }

  if (!actor) {
    return (
      <div className="platform-owner-gate">
        <section role="alert">
          <i className="fas fa-shield-halved" aria-hidden="true" />
          <span className="platform-eyebrow">Central Nivora ERP</span>
          <h1>Sessão administrativa encerrada</h1>
          <p>{sessionFailure || 'Entre novamente para continuar.'}</p>
          <button type="button" className="platform-button is-primary" onClick={() => navigateTo('/owner/login', onNavigate)}>Ir para o login</button>
        </section>
      </div>
    );
  }

  return (
    <div className="platform-owner-page">
      <header className="platform-owner-topbar">
        <a className="platform-owner-brand" href="/owner" onClick={(event) => { event.preventDefault(); setActiveTab('overview'); }}>
          <img className="platform-owner-brand-logo" src="/assets/nivora-logo.png" alt="Nivora ERP" />
          <span><strong>Central SaaS</strong><small>Gestão da plataforma</small></span>
        </a>
        <div className="platform-owner-user">
          <span><strong>{user.displayName || user.display_name || user.email}</strong><small>Owner da plataforma</small></span>
          <button type="button" onClick={signOut}><i className="fas fa-arrow-right-from-bracket" aria-hidden="true" /> Sair</button>
        </div>
      </header>

      <div className="platform-owner-shell">
        <aside className="platform-owner-nav" aria-label="Áreas da central Nivora ERP">
          <div>
            <span className="platform-nav-label">Operação SaaS</span>
            {TABS.map((tab) => (
              <button key={tab.id} type="button" className={activeTab === tab.id ? 'is-active' : ''} onClick={() => setActiveTab(tab.id)}>
                <i className={`fas ${tab.icon}`} aria-hidden="true" /><span>{tab.label}</span>
              </button>
            ))}
          </div>
          <p><i className="fas fa-lock" aria-hidden="true" /> Esta central não acessa a operação local dos lojistas.</p>
        </aside>

        <main className="platform-owner-content">
          <div className="platform-page-heading">
            <div><span className="platform-eyebrow">Nivora ERP · uso interno</span><h1>{TABS.find((tab) => tab.id === activeTab)?.label}</h1></div>
            <button type="button" className="platform-button is-secondary" onClick={() => {
              if (activeTab === 'overview') void loadOverview();
              if (activeTab === 'tenants') void loadTenants();
              if (activeTab === 'subscriptions') void loadSubscriptions();
              if (activeTab === 'affiliates') setAffiliateRefreshToken((value) => value + 1);
              if (activeTab === 'banners') {
                void loadBannerTenants();
                setBannerRefreshToken((value) => value + 1);
              }
              if (activeTab === 'tickets') void loadTickets();
            }} disabled={loadingSection}><i className={`fas ${loadingSection ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true" /> Atualizar</button>
          </div>

          <Feedback value={feedback} onDismiss={() => setFeedback(null)} />

          {activeTab === 'overview' && <OverviewSection data={overview} loading={loadingSection} onNavigate={setActiveTab} />}
          {activeTab === 'tenants' && (
            <TenantsSection
              items={tenants}
              filters={tenantFilters}
              setFilters={setTenantFilters}
              loading={loadingSection}
              onSearch={loadTenants}
              onEdit={setEditingTenant}
            />
          )}
          {activeTab === 'subscriptions' && (
            <SubscriptionsSection
              items={subscriptions}
              filters={subscriptionFilters}
              setFilters={setSubscriptionFilters}
              loading={loadingSection}
              onSearch={loadSubscriptions}
              onEdit={setEditingSubscription}
              tenants={tenants}
            />
          )}
          {activeTab === 'affiliates' && (
            <PlatformAffiliateManager refreshToken={affiliateRefreshToken} />
          )}
          {activeTab === 'banners' && (
            <PlatformBannerManager
              tenants={tenants}
              loadingTenants={loadingSection}
              refreshToken={bannerRefreshToken}
            />
          )}
          {activeTab === 'tickets' && (
            <TicketsSection
              items={tickets}
              filters={ticketFilters}
              setFilters={setTicketFilters}
              loading={loadingSection}
              onSearch={loadTickets}
              selected={selectedTicket}
              onSelect={openTicket}
              onClose={() => setSelectedTicket(null)}
              onChanged={async (message) => {
                setFeedback({ type: 'success', text: message });
                await loadTickets();
                if (selectedTicket?.id) await openTicket(selectedTicket);
              }}
            />
          )}
        </main>
      </div>

      {editingTenant && (
        <TenantEditor
          tenant={editingTenant}
          onClose={() => setEditingTenant(null)}
          onTenantChanged={async () => {
            await Promise.all([loadTenants(), loadOverview()]);
          }}
          onSaved={async () => {
            setEditingTenant(null);
            setFeedback({ type: 'success', text: 'Cliente, plano e licença atualizados.' });
            await Promise.all([loadTenants(), loadOverview()]);
          }}
        />
      )}
      {editingSubscription && (
        <SubscriptionEditor
          subscription={editingSubscription}
          onClose={() => setEditingSubscription(null)}
          onSaved={async () => {
            setEditingSubscription(null);
            setFeedback({ type: 'success', text: 'Assinatura atualizada.' });
            await Promise.all([loadSubscriptions(), loadOverview()]);
          }}
        />
      )}
    </div>
  );
}

function OverviewSection({ data, loading, onNavigate }) {
  if (loading && !data) return <SectionLoading />;
  if (!data) return <EmptyState icon="fa-chart-column" title="Indicadores indisponíveis">Atualize a página para consultar a operação central.</EmptyState>;
  const tenants = data.tenants || {};
  const subscriptions = data.subscriptions || {};
  const tickets = data.tickets || {};
  const users = data.users || {};
  return (
    <>
      <section className="platform-metric-grid" aria-label="Indicadores da plataforma">
        <Metric label="Clientes ativos" value={tenants.active || 0} detail={`${tenants.total || 0} cadastrados`} icon="fa-building-circle-check" tone="green" />
        <Metric label="MRR manual" value={formatPlatformMoney(subscriptions.mrrCents || subscriptions.mrr_cents)} detail={`${subscriptions.active || 0} assinaturas ativas`} icon="fa-arrow-trend-up" tone="blue" />
        <Metric label="Em atraso" value={subscriptions.pastDue || subscriptions.past_due || 0} detail="assinaturas para acompanhar" icon="fa-calendar-xmark" tone="amber" />
        <Metric label="Tickets abertos" value={tickets.open || 0} detail={`${tickets.urgent || 0} urgentes`} icon="fa-headset" tone="red" />
      </section>
      <section className="platform-overview-grid">
        <article className="platform-card">
          <header><div><span className="platform-eyebrow">Carteira</span><h2>Saúde dos clientes</h2></div><button type="button" onClick={() => onNavigate('tenants')}>Ver clientes</button></header>
          <div className="platform-health-list">
            <div><span><i className="fas fa-circle is-green" /> Ativos</span><strong>{tenants.active || 0}</strong></div>
            <div><span><i className="fas fa-circle is-amber" /> Suspensos</span><strong>{tenants.suspended || 0}</strong></div>
            <div><span><i className="fas fa-circle is-gray" /> Inativos</span><strong>{tenants.inactive || 0}</strong></div>
            <div><span><i className="fas fa-users-gear" /> Operadores da central</span><strong>{users.active || 0}</strong></div>
          </div>
        </article>
        <article className="platform-card">
          <header><div><span className="platform-eyebrow">Atendimento</span><h2>Fila de suporte</h2></div><button type="button" onClick={() => onNavigate('tickets')}>Abrir fila</button></header>
          <div className="platform-queue-highlight">
            <strong>{tickets.open || 0}</strong><span>chamados abertos</span>
            <div><b>{tickets.waitingCustomer || tickets.waiting_customer || 0}</b> aguardando cliente</div>
            <div className={(tickets.urgent || 0) > 0 ? 'is-alert' : ''}><b>{tickets.urgent || 0}</b> urgentes</div>
          </div>
        </article>
      </section>
    </>
  );
}

function Metric({ label, value, detail, icon, tone }) {
  return <article className={`platform-metric is-${tone}`}><span>{label}</span><strong>{value}</strong><small>{detail}</small><i className={`fas ${icon}`} aria-hidden="true" /></article>;
}

function SectionLoading() {
  return <div className="platform-section-loading" role="status"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Carregando dados centrais...</div>;
}

function FilterBar({ children, onSubmit }) {
  return <form className="platform-filter-bar" onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>{children}<button className="platform-button is-primary" type="submit"><i className="fas fa-magnifying-glass" aria-hidden="true" /> Filtrar</button></form>;
}

function TenantsSection({ items, filters, setFilters, loading, onSearch, onEdit }) {
  return (
    <section className="platform-card platform-data-card">
      <header><div><span className="platform-eyebrow">Carteira comercial</span><h2>Empresas clientes</h2></div><small>Plano, licença e assentos são centrais.</small></header>
      <FilterBar onSubmit={onSearch}>
        <label className="is-wide"><span>Buscar empresa</span><input value={filters.q} onChange={(e) => setFilters((v) => ({ ...v, q: e.target.value }))} placeholder="Nome, slug ou CNPJ" /></label>
        <label><span>Status</span><select value={filters.status} onChange={(e) => setFilters((v) => ({ ...v, status: e.target.value }))}><option value="">Todos</option>{Object.entries(TENANT_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        <label><span>Plano</span><select value={filters.plan} onChange={(e) => setFilters((v) => ({ ...v, plan: e.target.value }))}><option value="">Todos</option>{Object.entries(PLAN_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      </FilterBar>
      {loading ? <SectionLoading /> : items.length ? (
        <div className="platform-table-wrap"><table><thead><tr><th>Cliente</th><th>Status</th><th>Plano</th><th>Licença</th><th>Assentos</th><th aria-label="Ações" /></tr></thead><tbody>{items.map((tenant) => (
          <tr key={tenant.id}><td><strong>{tenant.displayName}</strong><small>{tenant.slug}{tenant.legalName && tenant.legalName !== tenant.displayName ? ` · ${tenant.legalName}` : ''}</small></td><td><StatusPill value={tenant.status} labels={TENANT_STATUS} /></td><td>{PLAN_LABELS[tenant.planCode] || tenant.planCode}</td><td><StatusPill value={tenant.licenseStatus} labels={LICENSE_STATUS} /></td><td>{tenant.occupiedSeats} / {tenant.includedSeats + tenant.extraSeats}<small>{tenant.extraSeats ? `${tenant.extraSeats} extra(s)` : 'sem extras'}</small></td><td><button className="platform-icon-button" type="button" onClick={() => onEdit(tenant)} aria-label={`Gerenciar ${tenant.displayName}`}><i className="fas fa-sliders" aria-hidden="true" /></button></td></tr>
        ))}</tbody></table></div>
      ) : <EmptyState icon="fa-building-circle-xmark" title="Nenhum cliente encontrado">Revise os filtros ou aguarde o primeiro cadastro.</EmptyState>}
    </section>
  );
}

function TenantEditor({ tenant, onClose, onSaved, onTenantChanged }) {
  const [activeTab, setActiveTab] = useState('account');
  const capacity = tenant.includedSeats + tenant.extraSeats;
  return (
    <Modal title={`Gerenciar ${tenant.displayName}`} onClose={onClose} wide>
      <div className="platform-tenant-summary">
        <div><strong>{tenant.slug}</strong><span>{PLAN_LABELS[tenant.planCode] || tenant.planCode}</span></div>
        <div><strong>{tenant.occupiedSeats} / {capacity}</strong><span>usuários ativos</span></div>
        <div><StatusPill value={tenant.status} labels={TENANT_STATUS} /><span>situação do cliente</span></div>
      </div>
      <nav className="platform-tenant-tabs" role="tablist" aria-label="Gestão do cliente">
        {TENANT_EDITOR_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={`fas ${tab.icon}`} aria-hidden="true" /> {tab.label}
          </button>
        ))}
      </nav>
      <div className="platform-tenant-tab-content" role="tabpanel">
        {activeTab === 'account' && <TenantAccountSection tenant={tenant} onClose={onClose} onSaved={onSaved} />}
        {activeTab === 'users' && <TenantUsersSection tenant={tenant} onTenantChanged={onTenantChanged} />}
        {activeTab === 'modules' && <TenantModulesSection tenant={tenant} />}
      </div>
    </Modal>
  );
}

function TenantAccountSection({ tenant, onClose, onSaved }) {
  const [form, setForm] = useState({ status: tenant.status, planCode: tenant.planCode, licenseStatus: tenant.licenseStatus, persistenceMode: tenant.persistenceMode || 'cloud_authoritative', extraSeats: String(tenant.extraSeats), billingReference: tenant.billingReference });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault(); setSaving(true); setError('');
    try {
      if (form.status !== tenant.status) await updatePlatformTenantStatus(tenant.id, form.status);
      if (form.planCode !== tenant.planCode) await updatePlatformTenantPlan(tenant.id, form.planCode);
      if (form.licenseStatus !== tenant.licenseStatus || form.persistenceMode !== tenant.persistenceMode || Number(form.extraSeats) !== tenant.extraSeats || form.billingReference !== tenant.billingReference) {
        await updatePlatformTenantLicense(tenant.id, { status: form.licenseStatus, persistenceMode: form.persistenceMode, extraSeats: Math.max(0, Number(form.extraSeats || 0)), billingReference: form.billingReference.trim() || null });
      }
      await onSaved();
    } catch (saveError) { setError(saveError.message); } finally { setSaving(false); }
  };
  return (
    <form className="platform-editor-form platform-tenant-account-form" onSubmit={submit}>
      <p className="platform-editor-context"><strong>Configuração comercial</strong><span>Os {tenant.includedSeats} primeiros usuários estão incluídos. Reduza assentos extras somente depois de desativar acessos excedentes.</span></p>
      <div className="platform-form-grid"><SelectField label="Status do cliente" value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} options={TENANT_STATUS} /><SelectField label="Plano" value={form.planCode} onChange={(v) => setForm((f) => ({ ...f, planCode: v }))} options={PLAN_LABELS} /><SelectField label="Status da licença" value={form.licenseStatus} onChange={(v) => setForm((f) => ({ ...f, licenseStatus: v }))} options={LICENSE_STATUS} /><SelectField label="Persistência" value={form.persistenceMode} onChange={(v) => setForm((f) => ({ ...f, persistenceMode: v }))} options={PERSISTENCE_MODE} /><label><span>Assentos extras</span><input type="number" min="0" max="999" value={form.extraSeats} onChange={(e) => setForm((f) => ({ ...f, extraSeats: e.target.value }))} required /></label><label className="is-full"><span>Referência de cobrança</span><input value={form.billingReference} onChange={(e) => setForm((f) => ({ ...f, billingReference: e.target.value }))} maxLength="180" placeholder="Contrato, cobrança ou observação curta" /></label></div>
      {error && <Feedback value={{ type: 'error', text: error }} />}
      <ModalActions saving={saving} onClose={onClose} />
    </form>
  );
}

function TenantUsersSection({ tenant, onTenantChanged }) {
  const [users, setUsers] = useState([]);
  const [license, setLicense] = useState(() => tenantLicenseView(null, tenant));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listPlatformTenantUsers(tenant.id, { limit: 100, offset: 0 });
      setUsers(itemsFrom(result, 'users').map(tenantUserView));
      setLicense(tenantLicenseView(result, tenant));
    } catch (error) {
      setFeedback({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => { void load(); }, [load]);

  const activeOwners = users.filter((user) => user.role === 'owner' && user.status === 'active').length;
  const runMutation = async (operation, successMessage) => {
    setSaving(true); setFeedback(null);
    try {
      await operation();
      setEditor(null);
      setFeedback({ type: 'success', text: successMessage });
      await load();
      if (typeof onTenantChanged === 'function') await onTenantChanged();
    } catch (error) {
      setFeedback({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const createUser = (input) => runMutation(
    () => createPlatformTenantUser(tenant.id, input),
    'Usuário criado. A senha inicial não ficará visível novamente.'
  );
  const updateUser = (user, input) => runMutation(
    () => updatePlatformTenantUser(tenant.id, user.id, input),
    input.status !== user.status ? 'Acesso e sessões do usuário atualizados.' : 'Dados do usuário atualizados.'
  );
  const resetPassword = (user, password) => runMutation(
    () => resetPlatformTenantUserPassword(tenant.id, user.id, password),
    'Nova senha definida e sessões anteriores encerradas.'
  );

  return (
    <section className="platform-tenant-panel platform-users-panel">
      <header className="platform-tenant-panel-heading">
        <div><span className="platform-eyebrow">Acessos nominativos</span><h3>Usuários deste cliente</h3><p>{license.occupiedSeats} de {license.capacity} assentos ativos · {license.availableSeats} disponível(is).</p></div>
        <button className="platform-button is-primary" type="button" disabled={loading || saving || license.availableSeats <= 0} onClick={() => setEditor({ type: 'create' })}><i className="fas fa-user-plus" aria-hidden="true" /> Novo usuário</button>
      </header>
      <div className="platform-seat-meter" aria-label={`${license.occupiedSeats} de ${license.capacity} assentos em uso`}><span style={{ width: `${license.capacity ? Math.min(100, (license.occupiedSeats / license.capacity) * 100) : 0}%` }} /></div>
      {license.availableSeats <= 0 && <div className="platform-inline-note is-warning"><i className="fas fa-circle-exclamation" aria-hidden="true" /> Todos os assentos estão ocupados. Libere um acesso ou contrate assento extra.</div>}
      <Feedback value={feedback} onDismiss={() => setFeedback(null)} />
      {editor?.type === 'create' && <TenantUserForm saving={saving} onCancel={() => setEditor(null)} onSubmit={createUser} />}
      {editor?.type === 'edit' && <TenantUserForm user={editor.user} saving={saving} activeOwners={activeOwners} atCapacity={license.availableSeats <= 0} onCancel={() => setEditor(null)} onSubmit={(input) => updateUser(editor.user, input)} />}
      {editor?.type === 'password' && <TenantPasswordReset user={editor.user} saving={saving} onCancel={() => setEditor(null)} onSubmit={(password) => resetPassword(editor.user, password)} />}
      {loading ? <SectionLoading /> : users.length ? (
        <div className="platform-tenant-users-list">
          {users.map((user) => (
            <article key={user.id}>
              <div className="platform-user-identity"><span className="platform-user-avatar" aria-hidden="true">{user.displayName.slice(0, 1).toUpperCase()}</span><span><strong>{user.displayName}</strong><small>{user.username.includes('@') ? user.username : `@${user.username}`}</small></span></div>
              <div><span className="platform-user-label">Perfil</span><strong>{TENANT_USER_ROLES[user.role] || user.role}</strong></div>
              <div><span className="platform-user-label">Último acesso</span><strong>{tenantUserActivity(user)}</strong></div>
              <div className="platform-user-status"><StatusPill value={tenantUserIsTemporarilyLocked(user) ? 'blocked' : user.status} labels={TENANT_USER_STATUS} /></div>
              <div className="platform-user-actions">
                <button type="button" onClick={() => setEditor({ type: 'edit', user })} aria-label={`Editar ${user.displayName}`} title="Editar usuário"><i className="fas fa-pen" aria-hidden="true" /></button>
                <button type="button" onClick={() => setEditor({ type: 'password', user })} aria-label={`Redefinir senha de ${user.displayName}`} title="Redefinir senha"><i className="fas fa-key" aria-hidden="true" /></button>
              </div>
            </article>
          ))}
        </div>
      ) : <EmptyState icon="fa-user-slash" title="Nenhum usuário encontrado">Crie o primeiro acesso nominativo desta empresa.</EmptyState>}
    </section>
  );
}

function TenantUserForm({ user = null, saving, activeOwners = 0, atCapacity = false, onCancel, onSubmit }) {
  const editing = Boolean(user);
  const [form, setForm] = useState({ username: user?.username || '', displayName: user?.displayName || '', role: user?.role || 'operator', status: user?.status || 'active', password: '' });
  const [error, setError] = useState('');
  const protectsLastOwner = editing && user.role === 'owner' && user.status === 'active' && activeOwners <= 1;
  const submit = (event) => {
    event.preventDefault(); setError('');
    const displayName = form.displayName.trim();
    const username = normalizeErpUsername(form.username);
    if (displayName.length < 2) { setError('Informe um nome com pelo menos 2 caracteres.'); return; }
    if (!editing && !isValidErpUsername(username)) { setError(ERP_USERNAME_HELP); return; }
    if (!editing && (form.password.length < 10 || !/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(form.password) || !/\d/.test(form.password))) { setError('A senha inicial deve ter pelo menos 10 caracteres, com letras e números.'); return; }
    if (protectsLastOwner && (form.role !== 'owner' || form.status !== 'active')) { setError('Mantenha ao menos um proprietário ativo para este cliente.'); return; }
    if (editing && user.status !== 'active' && form.status === 'active' && atCapacity) { setError('Não há assento disponível para reativar este usuário.'); return; }
    onSubmit(editing
      ? { displayName, role: form.role, status: form.status }
      : { username, displayName, role: form.role, password: form.password });
  };
  return (
    <form className="platform-inline-editor" onSubmit={submit}>
      <header><div><strong>{editing ? `Editar ${user.displayName}` : 'Novo usuário'}</strong><small>{editing ? 'Login imutável; alterações de acesso são auditadas.' : 'Informe a senha inicial por canal seguro; ela não será exibida novamente.'}</small></div><button type="button" onClick={onCancel} aria-label="Fechar formulário"><i className="fas fa-xmark" /></button></header>
      <div className="platform-form-grid">
        <label><span>Login ou e-mail</span><input value={form.username} onChange={(event) => setForm((value) => ({ ...value, username: event.target.value }))} disabled={editing} autoCapitalize="none" autoComplete="username" maxLength="64" aria-describedby={editing ? undefined : 'tenant-user-login-help'} required />{!editing && <small id="tenant-user-login-help">{ERP_USERNAME_HELP}</small>}</label>
        <label><span>Nome de exibição</span><input value={form.displayName} onChange={(event) => setForm((value) => ({ ...value, displayName: event.target.value }))} maxLength="160" required /></label>
        <SelectField label="Perfil" value={form.role} onChange={(role) => setForm((value) => ({ ...value, role }))} options={TENANT_USER_ROLES} />
        {editing ? <SelectField label="Status" value={form.status} onChange={(status) => setForm((value) => ({ ...value, status }))} options={TENANT_USER_STATUS} /> : <label><span>Senha inicial</span><input type="password" value={form.password} onChange={(event) => setForm((value) => ({ ...value, password: event.target.value }))} autoComplete="new-password" minLength="10" maxLength="200" required /></label>}
      </div>
      {protectsLastOwner && <div className="platform-inline-note"><i className="fas fa-shield-halved" aria-hidden="true" /> Este é o último proprietário ativo e não pode perder acesso.</div>}
      {error && <Feedback value={{ type: 'error', text: error }} />}
      <div className="platform-inline-actions"><button className="platform-button is-ghost" type="button" onClick={onCancel} disabled={saving}>Cancelar</button><button className="platform-button is-primary" type="submit" disabled={saving}>{saving ? 'Salvando...' : editing ? 'Salvar usuário' : 'Criar usuário'}</button></div>
    </form>
  );
}

function TenantPasswordReset({ user, saving, onCancel, onSubmit }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const submit = (event) => {
    event.preventDefault(); setError('');
    if (password.length < 10 || !/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(password) || !/\d/.test(password)) { setError('A nova senha deve ter pelo menos 10 caracteres, com letras e números.'); return; }
    if (password !== confirmation) { setError('A confirmação não corresponde à nova senha.'); return; }
    onSubmit(password);
  };
  return (
    <form className="platform-inline-editor" onSubmit={submit}>
      <header><div><strong>Redefinir senha de {user.displayName}</strong><small>As sessões atuais serão encerradas. A senha não será exibida novamente.</small></div><button type="button" onClick={onCancel} aria-label="Fechar redefinição"><i className="fas fa-xmark" /></button></header>
      <div className="platform-form-grid"><label><span>Nova senha</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" minLength="10" maxLength="200" required /></label><label><span>Confirmar senha</span><input type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" minLength="10" maxLength="200" required /></label></div>
      {error && <Feedback value={{ type: 'error', text: error }} />}
      <div className="platform-inline-actions"><button className="platform-button is-ghost" type="button" onClick={onCancel} disabled={saving}>Cancelar</button><button className="platform-button is-primary" type="submit" disabled={saving}>{saving ? 'Redefinindo...' : 'Redefinir e encerrar sessões'}</button></div>
    </form>
  );
}

function TenantModulesSection({ tenant }) {
  const [modules, setModules] = useState([]);
  const [baseline, setBaseline] = useState({});
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const applyPayload = useCallback((payload, fallback = []) => {
    const rawItems = itemsFrom(payload, 'modules');
    const normalized = (Array.isArray(rawItems) && rawItems.length ? rawItems : fallback).map(tenantModuleView).filter((item) => item.key);
    setModules(normalized);
    setBaseline(Object.fromEntries(normalized.map((item) => [item.key, item.enabled])));
    setVersion(Number(valueFrom(payload, 'version', 'modulesVersion', 'modules_version') ?? 0));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setFeedback(null);
    try { applyPayload(await getPlatformTenantModules(tenant.id)); }
    catch (error) { setFeedback({ type: 'error', text: error.message }); }
    finally { setLoading(false); }
  }, [applyPayload, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const dirtyCount = modules.filter((module) => baseline[module.key] !== module.enabled).length;
  const grouped = useMemo(() => modules.reduce((result, module) => {
    const category = module.category || 'Operação';
    if (!result[category]) result[category] = [];
    result[category].push(module);
    return result;
  }, {}), [modules]);

  const toggle = (key) => setModules((current) => current.map((module) => module.key === key && !module.required ? { ...module, enabled: !module.enabled } : module));
  const save = async () => {
    setSaving(true); setFeedback(null);
    const pending = modules.map((module) => ({ key: module.key, enabled: module.required ? true : module.enabled }));
    try {
      const result = await updatePlatformTenantModules(tenant.id, {
        expectedVersion: version,
        modules: pending,
        reason: 'Configuração atualizada pela central Help Seller.'
      });
      applyPayload(result, modules);
      setFeedback({ type: 'success', text: 'Módulos atualizados de forma atômica.' });
    } catch (error) {
      setFeedback({ type: 'error', text: error.message });
    } finally { setSaving(false); }
  };

  return (
    <section className="platform-tenant-panel platform-modules-panel">
      <header className="platform-tenant-panel-heading"><div><span className="platform-eyebrow">Entitlements do tenant</span><h3>Módulos ativos</h3><p>As alterações entram juntas; módulos essenciais permanecem protegidos.</p></div><button className="platform-button is-primary" type="button" onClick={save} disabled={loading || saving || dirtyCount === 0}><i className="fas fa-floppy-disk" aria-hidden="true" /> {saving ? 'Salvando...' : `Salvar${dirtyCount ? ` (${dirtyCount})` : ''}`}</button></header>
      <Feedback value={feedback} onDismiss={() => setFeedback(null)} />
      {loading ? <SectionLoading /> : modules.length ? <div className="platform-module-groups">{Object.entries(grouped).map(([category, entries]) => (
        <section key={category}><header><h4>{category}</h4><span>{entries.filter((item) => item.enabled).length} de {entries.length} ativos</span></header>{entries.map((module) => (
          <label key={module.key} className={`platform-module-row ${module.required ? 'is-required' : ''}`}>
            <span className="platform-module-copy"><strong>{module.name}</strong><small>{module.description || 'Disponibilidade controlada para este cliente.'}</small><em>{module.required ? 'Essencial' : module.source === 'plan' ? 'Plano' : 'Configuração do tenant'}</em></span>
            <span className="platform-switch"><input type="checkbox" checked={module.required || module.enabled} disabled={module.required || saving} onChange={() => toggle(module.key)} /><span aria-hidden="true" /></span>
          </label>
        ))}</section>
      ))}</div> : <EmptyState icon="fa-puzzle-piece" title="Catálogo indisponível">Nenhum módulo foi devolvido para este cliente. <button className="platform-link-button" type="button" onClick={load}>Tentar novamente</button></EmptyState>}
      <div className="platform-modules-footer"><span>Versão da configuração: {version}</span><button className="platform-button is-ghost" type="button" onClick={load} disabled={loading || saving}><i className="fas fa-rotate" aria-hidden="true" /> Recarregar</button></div>
    </section>
  );
}

function SubscriptionsSection({ items, filters, setFilters, loading, onSearch, onEdit, tenants }) {
  const [newTenantId, setNewTenantId] = useState('');
  const subscribedIds = useMemo(() => new Set(items.map((item) => String(item.tenantId))), [items]);
  const availableTenants = tenants.filter((tenant) => !tenant.subscription && !subscribedIds.has(String(tenant.id)));
  const createForTenant = () => {
    const tenant = availableTenants.find((item) => String(item.id) === String(newTenantId));
    if (!tenant) return;
    onEdit(subscriptionView({
      tenantId: tenant.id,
      tenantName: tenant.displayName,
      planCode: tenant.planCode,
      billingCycle: 'monthly',
      priceCents: 4900,
      status: 'active',
      provider: 'manual'
    }));
  };
  return (
    <section className="platform-card platform-data-card">
      <header><div><span className="platform-eyebrow">Controle financeiro</span><h2>Assinaturas manuais</h2></div><small>Fonte central da vigência comercial.</small></header>
      {availableTenants.length > 0 && (
        <div className="platform-create-subscription">
          <label><span>Criar primeira assinatura</span><select value={newTenantId} onChange={(event) => setNewTenantId(event.target.value)}><option value="">Selecione um cliente sem assinatura</option>{availableTenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.displayName}</option>)}</select></label>
          <button type="button" className="platform-button is-secondary" disabled={!newTenantId} onClick={createForTenant}><i className="fas fa-plus" aria-hidden="true" /> Nova assinatura</button>
        </div>
      )}
      <FilterBar onSubmit={onSearch}><label><span>Status</span><select value={filters.status} onChange={(e) => setFilters((v) => ({ ...v, status: e.target.value }))}><option value="">Todos</option>{Object.entries(SUBSCRIPTION_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label><label><span>Plano</span><select value={filters.plan} onChange={(e) => setFilters((v) => ({ ...v, plan: e.target.value }))}><option value="">Todos</option>{Object.entries(PLAN_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label></FilterBar>
      {loading ? <SectionLoading /> : items.length ? <div className="platform-table-wrap"><table><thead><tr><th>Cliente</th><th>Plano</th><th>Ciclo</th><th>Valor</th><th>Status</th><th>Vencimento</th><th aria-label="Ações" /></tr></thead><tbody>{items.map((sub) => <tr key={sub.id || sub.tenantId}><td><strong>{sub.tenantName}</strong><small>{sub.provider || 'Gestão manual'}</small></td><td>{PLAN_LABELS[sub.planCode] || sub.planCode}</td><td>{sub.billingCycle === 'annual' ? 'Anual' : sub.billingCycle === 'one_time' ? 'À vista' : 'Mensal'}</td><td>{formatPlatformMoney(sub.priceCents)}</td><td><StatusPill value={sub.status} labels={SUBSCRIPTION_STATUS} /></td><td>{formatPlatformDate(sub.currentPeriodEndsAt)}</td><td><button className="platform-icon-button" type="button" onClick={() => onEdit(sub)} aria-label={`Editar assinatura de ${sub.tenantName}`}><i className="fas fa-pen" aria-hidden="true" /></button></td></tr>)}</tbody></table></div> : <EmptyState icon="fa-file-invoice-dollar" title="Nenhuma assinatura encontrada">Cadastre a condição comercial de cada cliente na fonte central.</EmptyState>}
    </section>
  );
}

function SubscriptionEditor({ subscription, onClose, onSaved }) {
  const [form, setForm] = useState({ planCode: subscription.planCode, billingCycle: subscription.billingCycle, price: (subscription.priceCents / 100).toFixed(2).replace('.', ','), status: subscription.status, currentPeriodEndsAt: dateInput(subscription.currentPeriodEndsAt), notes: subscription.notes || '' });
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const submit = async (event) => { event.preventDefault(); const priceCents = priceInputToCents(form.price); if (!Number.isFinite(priceCents)) { setError('Informe um valor mensal ou anual válido.'); return; } setSaving(true); setError(''); try { await updatePlatformSubscription(subscription.tenantId, { planCode: form.planCode, billingCycle: form.billingCycle, priceCents, status: form.status, startsAt: subscription.startsAt || null, currentPeriodEndsAt: isoEndOfDay(form.currentPeriodEndsAt), notes: form.notes.trim() || null, provider: subscription.provider || 'manual', providerSubscriptionId: subscription.providerSubscriptionId || null }); await onSaved(); } catch (saveError) { setError(saveError.message); } finally { setSaving(false); } };
  return <Modal title={`Assinatura · ${subscription.tenantName}`} onClose={onClose}><form className="platform-editor-form" onSubmit={submit}><div className="platform-form-grid"><SelectField label="Plano" value={form.planCode} onChange={(v) => setForm((f) => ({ ...f, planCode: v }))} options={PLAN_LABELS} /><SelectField label="Periodicidade" value={form.billingCycle} onChange={(v) => setForm((f) => ({ ...f, billingCycle: v }))} options={{ monthly: 'Mensal', annual: 'Anual', one_time: 'À vista' }} /><label><span>Valor (R$)</span><input inputMode="decimal" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} required /></label><SelectField label="Status" value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} options={SUBSCRIPTION_STATUS} /><label><span>Vencimento da vigência</span><input type="date" value={form.currentPeriodEndsAt} onChange={(e) => setForm((f) => ({ ...f, currentPeriodEndsAt: e.target.value }))} /></label><label className="is-full"><span>Observações internas</span><textarea rows="3" maxLength="1000" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></label></div>{error && <Feedback value={{ type: 'error', text: error }} />}<ModalActions saving={saving} onClose={onClose} /></form></Modal>;
}

function TicketsSection({ items, filters, setFilters, loading, onSearch, selected, onSelect, onClose, onChanged }) {
  return (
    <div className="platform-ticket-layout">
      <section className="platform-card platform-data-card platform-ticket-list"><header><div><span className="platform-eyebrow">Suporte</span><h2>Fila de tickets</h2></div></header><FilterBar onSubmit={onSearch}><label className="is-wide"><span>Buscar</span><input value={filters.q} onChange={(e) => setFilters((v) => ({ ...v, q: e.target.value }))} placeholder="Assunto, empresa ou número" /></label><label><span>Status</span><select value={filters.status} onChange={(e) => setFilters((v) => ({ ...v, status: e.target.value }))}><option value="">Todos</option>{Object.entries(TICKET_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label><label><span>Prioridade</span><select value={filters.priority} onChange={(e) => setFilters((v) => ({ ...v, priority: e.target.value }))}><option value="">Todas</option>{Object.entries(TICKET_PRIORITY).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label></FilterBar>{loading ? <SectionLoading /> : items.length ? <div className="platform-ticket-items">{items.map((ticket) => <button key={ticket.id} type="button" className={selected?.id === ticket.id ? 'is-active' : ''} onClick={() => onSelect(ticket)}><span className="platform-ticket-number">#{ticket.number || String(ticket.id).slice(0, 8)}</span><strong>{ticket.subject}</strong><small>{ticket.tenantName} · {formatPlatformDate(ticket.lastMessageAt, true)}</small><span><StatusPill value={ticket.status} labels={TICKET_STATUS} /><StatusPill value={ticket.priority} labels={TICKET_PRIORITY} /></span></button>)}</div> : <EmptyState icon="fa-inbox" title="Fila vazia">Nenhum ticket corresponde aos filtros selecionados.</EmptyState>}</section>
      <TicketDetail selected={selected} onClose={onClose} onChanged={onChanged} />
    </div>
  );
}

function TicketDetail({ selected, onClose, onChanged }) {
  const [status, setStatus] = useState('open'); const [priority, setPriority] = useState('normal'); const [reply, setReply] = useState(''); const [isInternal, setIsInternal] = useState(false); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  useEffect(() => { if (selected) { setStatus(selected.status || 'open'); setPriority(selected.priority || 'normal'); setReply(''); setIsInternal(false); setError(''); } }, [selected]);
  if (!selected) return <section className="platform-card platform-ticket-detail"><EmptyState icon="fa-comments" title="Selecione um ticket">O histórico e as ações aparecerão neste espaço.</EmptyState></section>;
  if (selected.loading) return <section className="platform-card platform-ticket-detail"><SectionLoading /></section>;
  const messages = itemsFrom(selected, 'messages');
  const saveState = async () => { setSaving(true); setError(''); try { await updatePlatformTicket(selected.id, { status, priority }); await onChanged('Status e prioridade do ticket atualizados.'); } catch (saveError) { setError(saveError.message); } finally { setSaving(false); } };
  const sendReply = async (event) => { event.preventDefault(); if (!reply.trim()) return; setSaving(true); setError(''); try { await replyPlatformTicket(selected.id, reply, { isInternal }); setReply(''); await onChanged(isInternal ? 'Nota interna registrada.' : 'Resposta enviada ao cliente.'); } catch (saveError) { setError(saveError.message); } finally { setSaving(false); } };
  return <section className="platform-card platform-ticket-detail"><header><div><span className="platform-eyebrow">Ticket #{selected.number || String(selected.id).slice(0, 8)}</span><h2>{selected.subject}</h2><small>{selected.tenantName}</small></div><button type="button" className="platform-icon-button" onClick={onClose} aria-label="Fechar detalhe"><i className="fas fa-xmark" /></button></header><div className="platform-ticket-controls"><SelectField label="Status" value={status} onChange={setStatus} options={TICKET_STATUS} /><SelectField label="Prioridade" value={priority} onChange={setPriority} options={TICKET_PRIORITY} /><button type="button" className="platform-button is-secondary" disabled={saving} onClick={saveState}>Salvar classificação</button></div><div className="platform-message-thread">{messages.length ? messages.map((message, index) => { const author = valueFrom(message, 'authorType', 'author_type') || 'system'; const internal = Boolean(valueFrom(message, 'internal', 'isInternal', 'is_internal')); return <article key={message.id || index} className={`is-${author} ${internal ? 'is-internal' : ''}`}><header><strong>{valueFrom(message, 'authorName', 'author_name') || (author === 'platform_user' ? 'Help Seller' : author === 'tenant_user' ? selected.tenantName : 'Sistema')}</strong><span>{internal ? 'Nota interna · ' : ''}{formatPlatformDate(valueFrom(message, 'createdAt', 'created_at'), true)}</span></header><p>{valueFrom(message, 'body', 'message') || ''}</p></article>; }) : <EmptyState icon="fa-message" title="Sem mensagens">Este ticket ainda não possui histórico.</EmptyState>}</div><form className="platform-reply-box" onSubmit={sendReply}><label><span>{isInternal ? 'Nota interna' : 'Resposta ao cliente'}</span><textarea rows="4" value={reply} onChange={(e) => setReply(e.target.value)} maxLength="10000" placeholder={isInternal ? 'Somente a equipe Help Seller verá esta nota.' : 'Escreva uma orientação objetiva para o cliente.'} required /></label><div><label className="platform-checkbox"><input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} /><span>Salvar como nota interna</span></label><button className="platform-button is-primary" type="submit" disabled={saving || !reply.trim()}>{saving ? 'Salvando...' : isInternal ? 'Registrar nota' : 'Enviar resposta'}</button></div></form>{error && <Feedback value={{ type: 'error', text: error }} />}</section>;
}

function Modal({ title, onClose, children, wide = false }) {
  useEffect(() => { const close = (event) => { if (event.key === 'Escape') onClose(); }; window.addEventListener('keydown', close); return () => window.removeEventListener('keydown', close); }, [onClose]);
  return <div className="platform-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}><section className={`platform-modal ${wide ? 'is-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}><header><div><span className="platform-eyebrow">Gestão central</span><h2>{title}</h2></div><button type="button" onClick={onClose} aria-label="Fechar"><i className="fas fa-xmark" /></button></header>{children}</section></div>;
}

function SelectField({ label, value, onChange, options }) {
  return <label><span>{label}</span><select value={value} onChange={(e) => onChange(e.target.value)}>{Object.entries(options).map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}</select></label>;
}

function ModalActions({ saving, onClose }) {
  return <div className="platform-modal-actions"><button className="platform-button is-ghost" type="button" onClick={onClose} disabled={saving}>Cancelar</button><button className="platform-button is-primary" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar alterações'}</button></div>;
}
